import { AccordionOptionsType } from "@/types/ModuleOptionsType";
import { AccordionItems } from "./AccordionItems";
import { customSanitize } from "../../utils/customSanitize";
import { Modul_Akkordeon } from "../../wp/types/acf-module-types";
import { useSublinesVisibility } from "../../hooks/useSublinesOverlinesVisibility";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Section } from "@/components/Section/Section";
import { Headline } from "@/components/Headline/Headline";
import { AccordionDataFetched } from "@/getData/getIndexedAccordionData";
import { Button } from "@/components/Button/Button";
import { useState } from "react";
import { AccordionFilter } from "./AccordionFilter/AccordionFilter";
import styles from "./Accordion.module.scss";

export type AccordionModuleType = Modul_Akkordeon;

export type AccordionElementType = {
  title: string;
  content: string;
};

interface AccordionProps {
  data: AccordionModuleType;
  accordionOptions: AccordionOptionsType;
  index: number;
  accordionData?: AccordionDataFetched;
}
const Accordion = ({
  data,
  accordionOptions,
  index,
  accordionData,
}: AccordionProps) => {
  const [activeCategory, setActiveCategory] = useState<number | null>(null);

  const {
    id,
    section_padding_class,
    section_margin_class,
    background_color_class,
    module_width,
    headline_text_align_class,
  } = data.layout;

  const {
    border_line,
    separator,
    box_color,
    show_overline_subline,
    button_type,
    show_extended_link,
  } = accordionOptions ?? {};

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline,
  );

  const {
    headline,
    headline_html_tag,
    overline,
    content,
    elements,
    subline,
    link,
    show_filter,
    type,
  } = data.accordion;
  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);

  if (isHidden) {
    return null;
  }

  // Determine elements to show
  // If manual, use existing elements.
  // If faq or categories-faq, use accordionData.
  let elementsToShow = elements || [];

  if (type !== "manual" && accordionData) {
    if (show_filter && accordionData.categories) {
      if (activeCategory === null) {
        // Show all FAQs from all fetched categories
        // We can use accordionData.faqs which contains all items flattened
        // However, we must ensure the structure matches what we expect
        elementsToShow = accordionData.faqs.map((f) => ({
          title: f.title,
          content: f.content,
          link_extended: {
            type: "link" as const,
            link: null,
            download: {
              link_target: false,
              link_text: "",
              link_aria_label: "",
            },
            email: { link_target: "", link_text: "", link_aria_label: "" },
            telephone: { link_target: "", link_text: "", link_aria_label: "" },
            pop_up: { link_text: "", link_aria_label: "" },
          },
        }));
      } else {
        // Filter by active category
        const categoryData = accordionData.categories.find(
          (c) => c.id === activeCategory,
        );
        if (categoryData) {
          elementsToShow = categoryData.faqs.map((f) => ({
            title: f.title,
            content: f.content,
            link_extended: {
              type: "link" as const,
              link: null,
              download: {
                link_target: false,
                link_text: "",
                link_aria_label: "",
              },
              email: { link_target: "", link_text: "", link_aria_label: "" },
              telephone: {
                link_target: "",
                link_text: "",
                link_aria_label: "",
              },
              pop_up: { link_text: "", link_aria_label: "" },
            },
          }));
        } else {
          elementsToShow = [];
        }
      }
    } else {
      elementsToShow = accordionData.faqs.map((f) => ({
        title: f.title,
        content: f.content,
        link_extended: {
          type: "link" as const,
          link: null,
          download: { link_target: false, link_text: "", link_aria_label: "" },
          email: { link_target: "", link_text: "", link_aria_label: "" },
          telephone: { link_target: "", link_text: "", link_aria_label: "" },
          pop_up: { link_text: "", link_aria_label: "" },
        },
      }));
    }
  }

  return (
    <Section
      className={`accordion ${styles["accordion-section"]} ${module_width} ${background_color_class} ${section_padding_class} ${section_margin_class}`}
      columnsClassName="is-multiline is-centered"
      frameless={module_width === "frameless"}
      disableClip
      id={id}
    >
      <div className="column is-4-desktop is-12-touch">
        {/* jscpd:ignore-start */}
        <Headline
          headline_html_tag={
            headline_html_tag === "h2-small" ? "h2" : headline_html_tag
          }
          headline={headline}
          overline={showOverline ? overline : ""}
          subline={showSubline ? subline : ""}
          extraClass={headline_text_align_class}
          wrapInSection={module_width === "frameless"}
          disableAnimation
        />

        {content && (
          <div dangerouslySetInnerHTML={{ __html: customSanitize(content) }} />
        )}

        {link && !show_filter && (
          <Button
            {...link}
            aria-label={link.aria_label}
            type={button_type}
            href={link.url}
          >
            {link.title}
          </Button>
        )}

        {show_filter && accordionData?.categories && (
          <AccordionFilter
            categories={accordionData.categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        )}
        {/* jscpd:ignore-end */}
      </div>

      <div
        className="column is-8-desktop is-12-touch"
        aria-live="polite"
        aria-atomic="false"
      >
        <AccordionItems
          elements={elementsToShow}
          // If we have categories data and show_filter is true, we might want to pass it to AccordionItems if it supports it in future
          // For now, adhering to existing props.
          border_line={{
            color: border_line.color || "",
            height: border_line.height || 0,
          }}
          separator={separator}
          box_color={box_color || ""}
          moduleIndex={index}
          showExtendedLink={show_extended_link}
          buttonType={button_type}
        />
      </div>
    </Section>
  );
};

export default Accordion;
